import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Row, Spinner } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";

import { useGetAllCategories } from "../../../../../api/jeep-rally/categories/categories.hooks";
import {
  useGetRouteCheckpoints,
  useReplaceRouteCheckpointsMutation,
  useUploadCheckpointImageMutation,
} from "../../../../../api/jeep-rally/checkpoints/checkpoints.hooks";
import {
  useGetRallyById,
  useGetRallyStages,
} from "../../../../../api/jeep-rally/rally/rally.hooks";
import { useGetRouteById } from "../../../../../api/jeep-rally/routes/routes.hooks";
import {
  getCategoryRoute,
  isJeepRallyAdmin,
  normalizeCategories,
} from "../../Categories/utils/category.utils";
import StageCheckpointsEditor from "../../Routes/_components/StageCheckpointsEditor";
import {
  groupCheckpointsByStage,
  normalizeCheckpoints,
  syncStageCheckpoints,
  validateStageCheckpoints,
} from "../../Routes/utils/checkpoint.utils";
import { mapStageToForm } from "../../Routes/utils/route.utils";
import {
  getCategorySelectValue,
  getRallyApiErrorMessage,
  getRallyCategories,
  getRallyCategoryLabel,
  normalizeRally,
} from "../utils/rally.utils";

const getEntityId = (value) => {
  if (!value) return "";
  if (typeof value === "string") return value;
  return value._id || value.id || "";
};

const resolveRallyCategories = (rally, allCategories = []) => {
  const raw = getRallyCategories(rally);
  if (!raw.length) return [];

  return raw
    .map((item) => {
      if (item && typeof item === "object" && (item._id || item.id || item.key)) {
        const id = item._id || item.id;
        if (item.route_id || item.route) return item;
        const match = allCategories.find(
          (category) =>
            getEntityId(category) === id ||
            category.key === item.key ||
            category.key === getCategorySelectValue(item)
        );
        return match ? { ...match, ...item } : item;
      }

      const token = String(item || "").trim();
      if (!token) return null;
      return (
        allCategories.find(
          (category) =>
            getEntityId(category) === token || category.key === token
        ) || null
      );
    })
    .filter(Boolean);
};

const getEventIdFromStage = (stage) => {
  const raw = stage?.event_id || stage?.eventId || stage?.event;
  return getEntityId(raw);
};

/**
 * Prefer stages attached to this rally; fall back to all route stages.
 */
const pickStagesForRally = (routeStages = [], rallyStages = [], rallyId) => {
  const routeList = Array.isArray(routeStages) ? routeStages : [];
  const rallyList = Array.isArray(rallyStages) ? rallyStages : [];

  if (rallyList.length) {
    const routeIds = new Set(
      routeList.map((stage) => getEntityId(stage)).filter(Boolean)
    );
    const matched = rallyList.filter((stage) => {
      const id = getEntityId(stage);
      return id && (routeIds.size === 0 || routeIds.has(id));
    });
    if (matched.length) return matched;
    return rallyList;
  }

  if (!rallyId) return routeList;

  const linked = routeList.filter(
    (stage) => getEventIdFromStage(stage) === String(rallyId)
  );
  return linked.length ? linked : routeList;
};

const buildEditorStages = (stages, categoryId, checkpoints) => {
  const grouped = groupCheckpointsByStage(checkpoints, {
    preferredCategoryId: categoryId,
  });

  return (stages || []).map((stage) => {
    const formStage = mapStageToForm(stage);
    const stageId = formStage._id;
    const match = stageId ? grouped[stageId] : null;
    return {
      ...formStage,
      checkpoint_category_id: categoryId,
      checkpoints: match?.checkpoints || [],
    };
  });
};

const RallyCheckpointsPage = () => {
  const history = useHistory();
  const { id: rallyId } = useParams();
  const canManage = isJeepRallyAdmin();

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [stages, setStages] = useState([]);
  const [stageErrors, setStageErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [hydratedKey, setHydratedKey] = useState("");

  const {
    data: rallyData,
    isLoading: isRallyLoading,
    isError: isRallyError,
    error: rallyError,
  } = useGetRallyById(rallyId);

  const rally = useMemo(() => normalizeRally(rallyData), [rallyData]);

  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useGetAllCategories();
  const allCategories = useMemo(
    () => normalizeCategories(categoriesData),
    [categoriesData]
  );

  const rallyCategories = useMemo(
    () => resolveRallyCategories(rally, allCategories),
    [rally, allCategories]
  );

  const selectedCategory = useMemo(
    () =>
      rallyCategories.find(
        (category) => getEntityId(category) === selectedCategoryId
      ) || null,
    [rallyCategories, selectedCategoryId]
  );

  const routeId = useMemo(() => {
    const route = getCategoryRoute(selectedCategory);
    return getEntityId(route);
  }, [selectedCategory]);

  const {
    data: routeData,
    isLoading: isRouteLoading,
    isError: isRouteError,
    error: routeError,
  } = useGetRouteById(routeId, { enabled: Boolean(routeId) });

  const route = useMemo(() => {
    if (!routeData) return null;
    if (routeData._id || routeData.id || routeData.title) return routeData;
    return routeData.data || routeData.route || null;
  }, [routeData]);

  const { data: rallyStagesData, isLoading: isRallyStagesLoading } =
    useGetRallyStages(rallyId, { enabled: Boolean(rallyId) });

  const rallyStages = useMemo(() => {
    if (Array.isArray(rallyStagesData)) return rallyStagesData;
    return (
      rallyStagesData?.data ||
      rallyStagesData?.stages ||
      rallyStagesData?.items ||
      []
    );
  }, [rallyStagesData]);

  const {
    data: checkpointsData,
    isLoading: isCheckpointsLoading,
    refetch: refetchCheckpoints,
  } = useGetRouteCheckpoints(
    routeId,
    { category_id: selectedCategoryId },
    { enabled: Boolean(routeId) && Boolean(selectedCategoryId) }
  );

  const {
    mutateAsync: replaceCheckpoints,
    isLoading: isReplacing,
    isPending: isReplacePending,
  } = useReplaceRouteCheckpointsMutation();

  const {
    mutateAsync: uploadCheckpointImage,
    isLoading: isUploadingImage,
    isPending: isUploadPending,
  } = useUploadCheckpointImageMutation();

  const submitting =
    isReplacing || isReplacePending || isUploadingImage || isUploadPending;

  useEffect(() => {
    if (!selectedCategoryId && rallyCategories.length === 1) {
      setSelectedCategoryId(getEntityId(rallyCategories[0]));
    }
  }, [rallyCategories, selectedCategoryId]);

  useEffect(() => {
    if (!selectedCategoryId || !routeId) {
      setStages([]);
      setHydratedKey("");
      return;
    }

    if (isRouteLoading || isRallyStagesLoading || isCheckpointsLoading) {
      return;
    }

    const key = `${routeId}:${selectedCategoryId}:${(
      route?.stages || []
    ).length}:${rallyStages.length}:${normalizeCheckpoints(checkpointsData).length}`;

    if (key === hydratedKey) return;

    const sourceStages = pickStagesForRally(
      route?.stages || [],
      rallyStages,
      rallyId
    );
    const checkpoints = normalizeCheckpoints(checkpointsData).filter((cp) => {
      const cpCategory = getEntityId(cp?.category_id || cp?.categoryId);
      return !cpCategory || cpCategory === selectedCategoryId;
    });

    setStages(buildEditorStages(sourceStages, selectedCategoryId, checkpoints));
    setStageErrors({});
    setHydratedKey(key);
  }, [
    selectedCategoryId,
    routeId,
    route,
    rallyStages,
    rallyId,
    checkpointsData,
    isRouteLoading,
    isRallyStagesLoading,
    isCheckpointsLoading,
    hydratedKey,
  ]);

  const updateStage = (index, patch) => {
    setStages((prev) =>
      prev.map((stage, i) =>
        i === index
          ? {
              ...stage,
              ...patch,
              checkpoint_category_id: selectedCategoryId,
            }
          : stage
      )
    );
    setStageErrors((prev) => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setSubmitSuccess("");
  };

  const handleCategoryChange = (nextId) => {
    setSelectedCategoryId(nextId);
    setStages([]);
    setHydratedKey("");
    setStageErrors({});
    setSubmitError("");
    setSubmitSuccess("");
  };

  const handleSave = async () => {
    if (!canManage || !routeId || !selectedCategoryId) return;

    setSubmitError("");
    setSubmitSuccess("");

    const nextErrors = {};
    stages.forEach((stage, index) => {
      const errors = validateStageCheckpoints({
        ...stage,
        checkpoint_category_id: selectedCategoryId,
      });
      if (Object.keys(errors).length) {
        nextErrors[index] = errors;
      }
    });
    setStageErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const stagesMissingId = stages.filter((stage) => !stage._id);
    if (stagesMissingId.length) {
      setSubmitError(
        "Some stages are missing ids. Attach stages to this rally/route first, then retry."
      );
      return;
    }

    try {
      await syncStageCheckpoints({
        routeId,
        stages: stages.map((stage) => ({
          ...stage,
          checkpoint_category_id: selectedCategoryId,
        })),
        replaceCheckpoints,
        uploadImage: uploadCheckpointImage,
      });
      setSubmitSuccess("Checkpoints saved successfully.");
      setHydratedKey("");
      await refetchCheckpoints();
    } catch (error) {
      setSubmitError(
        getRallyApiErrorMessage(error, "Failed to save checkpoints.")
      );
    }
  };

  const goBack = () => history.push("/jeep-rally/rallies");

  if (isRallyLoading || isCategoriesLoading) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-center py-5">
              <Spinner animation="border" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isRallyError || !rally) {
    return (
      <div className="container-fluid">
        <div className="card">
          <div className="card-body">
            <Alert variant="danger" className="mb-0">
              {getRallyApiErrorMessage(
                rallyError,
                "Rally not found. Open it from the rallies list."
              )}
            </Alert>
            <Button variant="outline-secondary" className="mt-3" onClick={goBack}>
              Back to rallies
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const loadingStages =
    Boolean(selectedCategoryId) &&
    (isRouteLoading || isRallyStagesLoading || isCheckpointsLoading);

  return (
    <div className="container-fluid">
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-2">
            <button
              type="button"
              className="btn btn-link p-0 text-decoration-none"
              onClick={goBack}
              aria-label="Back to rallies list"
              title="Back"
            >
              <i className="bi bi-arrow-left fs-4" />
            </button>
            <div>
              <h4 className="card-title mb-0">Rally Checkpoints</h4>
              <div className="text-muted small">{rally.name || rallyId}</div>
            </div>
          </div>
        </div>

        <div className="card-body">
          <p className="text-muted mb-4">
            Select a category for this rally, then edit checkpoints on each
            stage of that category&apos;s route (max 4 per stage).
          </p>

          {!canManage && (
            <Alert variant="warning" className="mb-4">
              Admin access is required to edit checkpoints.
            </Alert>
          )}

          {submitError && (
            <Alert variant="danger" className="mb-4">
              {submitError}
            </Alert>
          )}

          {submitSuccess && (
            <Alert variant="success" className="mb-4">
              {submitSuccess}
            </Alert>
          )}

          {!rallyCategories.length ? (
            <Alert variant="info" className="mb-0">
              This rally has no categories assigned. Edit the rally and assign
              categories (with a route) first.
            </Alert>
          ) : (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={selectedCategoryId}
                      onChange={(e) => handleCategoryChange(e.target.value)}
                      disabled={submitting}
                    >
                      <option value="">Select category</option>
                      {rallyCategories.map((category) => {
                        const id = getEntityId(category);
                        return (
                          <option key={id} value={id}>
                            {getRallyCategoryLabel(category) || id}
                          </option>
                        );
                      })}
                    </Form.Select>
                  </Form.Group>
                </Col>
                {selectedCategory && (
                  <Col md={6} className="d-flex align-items-end">
                    <div className="text-muted small pb-2">
                      Route:{" "}
                      {getCategoryRoute(selectedCategory)?.title ||
                        getCategoryRoute(selectedCategory)?.name ||
                        routeId ||
                        "Not linked"}
                    </div>
                  </Col>
                )}
              </Row>

              {selectedCategoryId && !routeId && (
                <Alert variant="warning">
                  Selected category has no route. Link a route on the category
                  before managing checkpoints.
                </Alert>
              )}

              {selectedCategoryId && routeId && isRouteError && (
                <Alert variant="danger">
                  {getRallyApiErrorMessage(
                    routeError,
                    "Failed to load the category route."
                  )}
                </Alert>
              )}

              {loadingStages ? (
                <div className="d-flex justify-content-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : null}

              {selectedCategoryId && routeId && !loadingStages ? (
                <>
                  {!stages.length ? (
                    <Alert variant="info">
                      No stages found for this rally/route. Create stages on the
                      route (and attach them to this rally) first.
                    </Alert>
                  ) : (
                    stages.map((stage, index) => (
                      <div
                        key={stage._id || `stage-${index}`}
                        className="border rounded p-3 mb-3"
                      >
                        <div className="mb-2">
                          <h6 className="mb-1">
                            Stage {stage.stage_number || index + 1}
                            {stage.stage_name ? ` — ${stage.stage_name}` : ""}
                          </h6>
                          <div className="text-muted small">
                            {[stage.starting_location, stage.ending_location]
                              .filter(Boolean)
                              .join(" → ") || "No locations set"}
                          </div>
                        </div>

                        <StageCheckpointsEditor
                          stage={stage}
                          stageIndex={index}
                          hideCategorySelect
                          disabled={submitting || !canManage}
                          errors={stageErrors[index] || {}}
                          onChange={(patch) => updateStage(index, patch)}
                        />
                      </div>
                    ))
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-3">
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={goBack}
                      disabled={submitting}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={
                        submitting || !canManage || !stages.length || !routeId
                      }
                      onClick={handleSave}
                    >
                      {submitting ? (
                        <>
                          <Spinner
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Saving...
                        </>
                      ) : (
                        "Save Checkpoints"
                      )}
                    </Button>
                  </div>
                </>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RallyCheckpointsPage;
